import Footer from "../components/footer";
import Header from "../components/header";
import { motion } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { getServices, getForwhos } from "../lib/api";
import { scrollToHash, scrollToHashDelayed } from "../utils/scrollToHash";
import type { Service, Forwho } from "../types/hygraph";

function Home() {
    const containerRef = useRef<HTMLDivElement>(null);
    const [services, setServices] = useState<Service[]>([]);
    const [forwhos, setForwhos] = useState<Forwho[]>([]);
    const [loading, setLoading] = useState(true);
    const location = useLocation();

    // Fetch data from Hygraph
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [servicesData, forwhosData] = await Promise.all([
                    getServices(),
                    getForwhos(),
                ]);
                setServices(servicesData);
                setForwhos(forwhosData);
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Handle scroll to hash when navigating from other pages
    useEffect(() => {
        if (location.hash) {
            // Wait for content to load and render
            // 300ms delay, 100px offset for fixed header
            scrollToHashDelayed(location.hash, 300, 100);
        } else {
            // If no hash, scroll to top
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [location]);

    // Handle smooth scroll with offset for anchor links on same page
    useEffect(() => {
        const handleAnchorClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const anchor = target.closest('a[href^="#"]');

            if (anchor && anchor instanceof HTMLAnchorElement) {
                const hash = anchor.getAttribute("href");
                if (hash && hash.startsWith("#")) {
                    e.preventDefault();
                    scrollToHash(hash, "smooth", 100);
                    // Update URL without triggering page reload
                    window.history.pushState(null, "", hash);
                }
            }
        };

        document.addEventListener("click", handleAnchorClick);
        return () => document.removeEventListener("click", handleAnchorClick);
    }, []);

    return (
        <>
            <Header />
            <section
                className="relative h-[75vh] flex flex-col items-center justify-center"
                id="heim"
            >
                <div className="absolute top-0 left-0 w-full h-full z-[-1]">
                    <img
                        src="/bg.webp"
                        alt="Dekoratives Bild. Weißer Schreibtisch mit Pflanze darauf in weißem Topf neben weißer Schreibtischlampe. Holzuhr an der weißen Wand"
                        className="w-full object-cover h-full"
                    />
                </div>
                <div className="text-center flex flex-col items-center">
                    <motion.h1
                        className="text-2xl md:text-4xl lg:text-5xl drop-shadow-md leading-none"
                        initial={{
                            opacity: 0,
                            y: 100,
                        }}
                        animate={{
                            opacity: 1,
                            y: 0,
                        }}
                        transition={{
                            duration: 1,
                        }}
                    >
                        Ihre Brücke <br />
                        zwischen Sprachen
                    </motion.h1>
                    <motion.p
                        className="mt-4 max-w-[42ch]"
                        initial={{
                            opacity: 0,
                            y: 100,
                        }}
                        animate={{
                            opacity: 1,
                            y: 0,
                        }}
                        transition={{
                            duration: 1,
                        }}
                    >
                        Professionelle Unterstützung auf Polnisch für
                        deutschsprachige Personen.
                    </motion.p>
                </div>
                <div className="w-full absolute bottom-0 left-0 flex flex-col justify-end">
                    <div className="mx-auto inline-flex gap-2 translate-y-1/2">
                        <a
                            className="px-4 py-2 bg-primary text-bg rounded-full"
                            href="#kontakt"
                        >
                            Kontakt
                        </a>
                        <a
                            className="px-4 py-2 bg-text text-bg rounded-full"
                            href="#dienstleistungen"
                        >
                            Finde mehr
                        </a>
                    </div>
                    <img
                        src="/rect-deco.svg"
                        className="mx-auto"
                        aria-hidden
                        alt="deco"
                    />
                </div>
            </section>
            <section
                className="custom-screen"
                ref={containerRef}
                id="dienstleistungen"
            >
                <div className="text-center flex flex-col items-center py-16">
                    <h1 className="text-2xl mb-4">Dienstleistungen</h1>
                    <p className="max-w-[56ch]">
                        Der Bereich meiner Dienstleistungen umfasst
                        hauptsächlich die Woiwodschaft Westpommern, insbesondere
                        Świnoujście und seine Umgebung. Ich bin hier, um Ihnen
                        das Leben zu erleichtern.
                    </p>
                </div>
                <div className="w-full h-px bg-text/20" />
                {loading ? (
                    <div className="px-[3vw] py-16 text-center">
                        <p>Laden...</p>
                    </div>
                ) : (
                    <div className="px-[3vw] py-16">
                        <div className="flex flex-col gap-16">
                            {services.map((service, index) => (
                                <motion.div
                                    className="flex flex-wrap w-full justify-between"
                                    key={service.id}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6 }}
                                    viewport={{ once: true }}
                                >
                                    <div className="flex flex-col gap-4 w-full">
                                        <div className="flex flex-wrap w-full justify-between">
                                            <h2 className="text-lg md:text-2xl font-bold max-w-[20ch]">
                                                {service.title}
                                            </h2>
                                            <p className="mt-2 max-w-[32ch]">
                                                {service.description}
                                            </p>
                                        </div>
                                        {/* Add links under first service */}
                                        {index === 0 && (
                                            <div className="flex flex-col md:flex-row gap-4 mt-4">
                                                <Link
                                                    to="/kooperationen?type=developer"
                                                    className="px-4 py-2 bg-primary text-bg rounded-full hover:bg-primary/90 transition-colors text-center"
                                                >
                                                    Angebote von Entwicklern
                                                </Link>
                                                <Link
                                                    to="/kooperationen?type=agency"
                                                    className="px-4 py-2 bg-text text-bg rounded-full hover:bg-text/90 transition-colors text-center"
                                                >
                                                    Angebote von Immobilienbüros
                                                </Link>
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                )}
                <div className="flex justify-center py-4">
                    <p className="font-deco text-lg md:text-xl">
                        Lösung alltäglicher Probleme{" "}
                    </p>
                </div>
            </section>
            <section className="relative" id="fur-wen">
                <img
                    src="/bg2.webp"
                    alt="Dekoratives Bild"
                    className="absolute top-0 left-0 w-full h-full z-[-1] py-px"
                />
                <div className="custom-screen">
                    <div className="w-full h-px bg-text/20" />
                    <div className="py-16 flex flex-col md:flex-row justify-between items-center px-[3vw]">
                        <div className="text-center md:text-left flex flex-col items-center md:items-start">
                            <h1 className="text-2xl mb-2">Für wen?</h1>
                            <p className="max-w-[42ch] mb-4">
                                Unsere Dienstleistungen sind für jeden gedacht,
                                der sich von den Herausforderungen einer neuen
                                Sprache und Kultur überwältigt fühlt.
                            </p>
                            <div className="flex gap-2">
                                <a
                                    className="px-4 py-2 bg-primary text-bg rounded-full"
                                    href="#kontakt"
                                >
                                    Kontakt
                                </a>
                                <a
                                    className="px-4 py-2 bg-text text-bg rounded-full"
                                    href="#uber-mich"
                                >
                                    Finde mehr
                                </a>
                            </div>
                        </div>
                        <div className="flex flex-col gap-x-8 md:flex-row flex-wrap justify-end">
                            {loading ? (
                                <p>Laden...</p>
                            ) : (
                                forwhos.map((forwho, idx) => {
                                    return (
                                        <div
                                            className="flex flex-col gap-2 mt-4 md:min-w-[45%]"
                                            key={forwho.id}
                                        >
                                            <span className="font-deco text-xl">
                                                {idx <= 9 ? 0 : null}
                                                {idx + 1}
                                            </span>
                                            <div className="w-full h-px bg-text" />
                                            <p>{forwho.title}</p>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                    <div className="w-full h-px bg-text/20" />
                </div>
            </section>
            <section
                className="bg-bg custom-screen flex items-center justify-between py-16 relative z-10"
                id="uber-mich"
            >
                <div className="w-full pb-16 md:pb-32">
                    <div className="flex flex-wrap items-end justify-between w-full">
                        <motion.h1
                            className="text-[20vmin] leading-none"
                            initial={{
                                opacity: 0,
                                y: 100,
                            }}
                            whileInView={{
                                opacity: 1,
                                y: 0,
                            }}
                            transition={{
                                duration: 1,
                            }}
                        >
                            Über
                            <br />
                            mich
                        </motion.h1>

                        <img src="/kz.webp" alt="Karolina Zielinska" />
                    </div>
                    <div className="h-px w-full bg-text/20 mb-4" />
                    <div className="flex w-full justify-between gap-24 md:gap-4 flex-wrap">
                        <div>
                            <p className="max-w-[68ch] mb-2">
                                Dank meiner umfangreichen Erfahrung in
                                Deutschland und meiner tiefen Leidenschaft für
                                die deutsche Sprache habe ich meine
                                Dienstleistungen so konzipiert, dass sie Ihnen
                                dabei helfen, sich problemlos in Polen
                                zurechtzufinden.
                            </p>
                            <p className="max-w-[67ch]">
                                Meine Mission besteht darin, eine reibungslose
                                Unterstützung in all Ihren Bedürfnissen in Polen
                                zu gewährleisten, unabhängig davon, ob Sie
                                bereits in Polen leben oder eine Umzug nach
                                Polen planen.
                            </p>
                        </div>
                        <div>
                            <h2 className="text-xl max-w-[32ch] mb-2">
                                Lassen Sie mich Ihr Führer und Unterstützer in
                                Polen sein.{" "}
                            </h2>
                            <p className="max-w-[56ch]">
                                Mein Fachwissen und meine Leidenschaft für die
                                deutsche Sprache ermöglichen es mir,
                                unvergleichliche Hilfe für deutschsprachige
                                Personen anzubieten, die sich im Leben in Polen
                                zurechtfinden.
                            </p>
                        </div>
                    </div>
                </div>
            </section>
            <Footer />
        </>
    );
}

export default Home;
